import React, { useEffect, useState } from 'react';
import {
  User, Archive, MessageSquare, Image as ImageIcon, FileText, FileType2,
  Sparkles, AlertTriangle, Info,
} from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { COMMON_PROFILE_FIELD_KEYS, COMMON_DOCUMENT_TYPES, IMAGE_TYPES_WITH_NO_EXTRACTABLE_FIELDS } from './automationConstants';
import { visionRoutingAPI } from '../../services/api';
import SendFormParamsEditor from './SendFormParamsEditor';

/**
 * Per-action_type parameter editor — extracted from AutomationRuleDialog's
 * former renderActionParameters so the flat form and the flow-graph editor
 * (AutomationFlowNodes.tsx) share one implementation instead of drifting.
 */

// 2026-09-10 (user: "too long and too confusing... do we need additional
// so much text?"). The panel used to explain every field with an
// always-visible sentence or two, which read as a wall of text. Moved the
// "why", not the "what", behind a small info icon + tooltip like this one
// -- the label + a one-line caption is enough to use the field; the
// rationale is still one hover away, not deleted.
const Hint: React.FC<{ text: string }> = ({ text }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Info className="h-3 w-3 text-muted-foreground/70 cursor-help" />
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-[260px] text-xs">
      {text}
    </TooltipContent>
  </Tooltip>
);

type ImageTypeRow = { key: string; description: string; live: boolean };

/**
 * 2026-09-10 (user: "for xray or skin there are no fields to be
 * extracted, how does it supposed to reflect it there?"). When the typed
 * document_type exactly matches a real Image Type Registry category
 * (skin_condition/chest_xray/lab_report — fetched live, not hardcoded, so
 * this always reflects what's actually configured on the account), shows
 * what ACTUALLY happens on upload: an automatic vision-model description
 * runs (app/services/media_router.py's route_requested_attachment),
 * completely separate from the "Fields to extract" panel below it. Same
 * fetch-once-on-mount pattern as AutomationFlowNodes.tsx's
 * SignalMultiSelect, kept as its own component so the network call only
 * fires while this hint is actually visible (Vault + a file answer type),
 * not on every render of every unrelated action type.
 *
 * Redesigned 2026-09-10 as a single compact pill (was a full paragraph) --
 * the detail moved into its tooltip.
 */
const VisionRoutingHint: React.FC<{ documentType: string }> = ({ documentType }) => {
  const [imageTypes, setImageTypes] = useState<ImageTypeRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    visionRoutingAPI
      .getImageTypes()
      .then((data: ImageTypeRow[]) => {
        if (!cancelled) setImageTypes(data);
      })
      .catch(() => {
        if (!cancelled) setImageTypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const key = documentType.trim();
  const match = imageTypes.find((it) => it.key === key);
  if (!key || !match) return null;

  if (!match.live) {
    return (
      <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-amber-300/60 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-400">
        <AlertTriangle className="h-3 w-3 shrink-0" />
        Not set up yet — configure it in Signals &amp; Image Routing
      </span>
    );
  }

  const hasNoFields = IMAGE_TYPES_WITH_NO_EXTRACTABLE_FIELDS.includes(key);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex w-fit cursor-help items-center gap-1.5 rounded-full border border-emerald-300/60 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-400">
          <Sparkles className="h-3 w-3 shrink-0" />
          Vision description runs automatically
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[280px] text-xs">
        "{key}" ({match.description}). Every upload gets visible observations and any urgent
        features flagged — never a diagnosis — independent of "Fields to extract" below.
        {hasNoFields && ' This photo type has no named text fields, so that section is hidden.'}
      </TooltipContent>
    </Tooltip>
  );
};

// 2026-09-10 (user: "then this string should be dropdown then no?" --
// correctly pointing out that free text here is a real typo risk: a
// misspelled document_type silently never reaches the vision-routed
// model, no error, no warning, since route_requested_attachment just
// treats an unrecognized category as a no-op). Picking a known image
// type (fetched live, same as VisionRoutingHint above) now guarantees
// exact spelling. "Other…" still allows free text for a genuine
// vault-only document type that ISN'T a vision-routed category at all
// (id_card, prescription, vaccination_card, or a new one) -- document_type
// was never meant to be restricted to just the 3 registry keys, only
// THOSE 3 specifically needed the typo protection.
const DocumentTypeField: React.FC<{ value: string; onChange: (v: string) => void; idPrefix: string }> = ({ value, onChange, idPrefix }) => {
  const [imageTypes, setImageTypes] = useState<ImageTypeRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    visionRoutingAPI
      .getImageTypes()
      .then((data: ImageTypeRow[]) => {
        if (!cancelled) setImageTypes(data);
      })
      .catch(() => {
        if (!cancelled) setImageTypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const trimmed = value.trim();
  const knownKeys = imageTypes.map((it) => it.key);
  const isKnown = knownKeys.includes(trimmed);
  const [otherMode, setOtherMode] = useState(!!trimmed && !isKnown);

  useEffect(() => {
    if (isKnown) setOtherMode(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKnown]);

  const selectValue = otherMode ? '__other__' : isKnown ? trimmed : '';

  return (
    <div className="space-y-1.5">
      <Select
        value={selectValue}
        onValueChange={(v) => {
          if (v === '__other__') {
            setOtherMode(true);
            onChange(isKnown ? '' : value);
          } else {
            setOtherMode(false);
            onChange(v);
          }
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder="Choose a document type" />
        </SelectTrigger>
        <SelectContent>
          {imageTypes.map((it) => (
            <SelectItem key={it.key} value={it.key}>
              {it.key}
              {!it.live && ' (not set up yet)'}
            </SelectItem>
          ))}
          <SelectItem value="__other__">Other…</SelectItem>
        </SelectContent>
      </Select>
      {otherMode && (
        <>
          <Input
            list={`${idPrefix}-document-type-suggestions`}
            placeholder="e.g. id_card, prescription"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
          <datalist id={`${idPrefix}-document-type-suggestions`}>
            {COMMON_DOCUMENT_TYPES.filter((t) => !knownKeys.includes(t)).map((type) => (
              <option key={type} value={type} />
            ))}
          </datalist>
        </>
      )}
    </div>
  );
};
interface Props {
  actionType: string;
  params: Record<string, any>;
  onChange: (paramKey: string, value: any) => void;
  isTicketEvent: boolean;
  agents: any[];
  teams: any[];
  labels: any[];
  // 2026-08-09: notify_agent/notify_manager's optional per-rule template/
  // channel override — same option shape AgentNotificationSettings.tsx uses.
  templates: Array<{ id: number; name: string; language?: string }>;
  channels: Array<{ id: number; name: string }>;
  idPrefix: string;
  // 2026-09-24: the rule's trigger -- only read by the Send form panel.
  triggerEvent?: string;
}

const AutomationActionParamsEditor: React.FC<Props> = ({
  actionType, params, onChange, isTicketEvent, agents, teams, labels, templates, channels, idPrefix, triggerEvent,
}) => {
  switch (actionType) {
    // 2026-09-24 (Docs/RELEASE_NOTES_2026-09-24_FORMS.md). Its
    // Completed / Declined paths reuse this same editor for each nested
    // action (render prop, so SendFormParamsEditor doesn't import this
    // file back).
    case 'send_form':
      return (
        <SendFormParamsEditor
          params={params}
          onChange={onChange}
          triggerEvent={triggerEvent}
          idPrefix={idPrefix}
          templates={templates}
          renderNestedParams={(nestedType, nestedParams, nestedOnChange, nestedId) => (
            <AutomationActionParamsEditor
              actionType={nestedType}
              params={nestedParams}
              onChange={nestedOnChange}
              isTicketEvent={isTicketEvent}
              agents={agents}
              teams={teams}
              labels={labels}
              templates={templates}
              channels={channels}
              idPrefix={nestedId}
            />
          )}
        />
      );

    case 'assign_agent':
      return (
        <div className="space-y-2">
          <Label className="text-xs">Agent</Label>
          <Select
            value={params.agent_id != null && params.agent_id !== '' ? String(params.agent_id) : ''}
            onValueChange={(v) => onChange('agent_id', v === '' ? '' : Number(v))}
          >
            <SelectTrigger><SelectValue placeholder="Select agent" /></SelectTrigger>
            <SelectContent>
              {agents.map((agent) => (
                <SelectItem key={agent.id} value={String(agent.id)}>
                  {agent.first_name} {agent.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case 'assign_team':
      return (
        <div className="space-y-2">
          <Label className="text-xs">Team</Label>
          <Select
            value={params.team_id != null && params.team_id !== '' ? String(params.team_id) : ''}
            onValueChange={(v) => onChange('team_id', v === '' ? '' : Number(v))}
          >
            <SelectTrigger><SelectValue placeholder="Select team" /></SelectTrigger>
            <SelectContent>
              {teams.map((team) => (
                <SelectItem key={team.id} value={String(team.id)}>{team.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case 'add_label':
    case 'remove_label':
      return (
        <div className="space-y-2">
          <Label className="text-xs">Label</Label>
          <Select
            value={params.label_id != null && params.label_id !== '' ? String(params.label_id) : ''}
            onValueChange={(v) => onChange('label_id', v === '' ? '' : Number(v))}
          >
            <SelectTrigger><SelectValue placeholder="Select label" /></SelectTrigger>
            <SelectContent>
              {labels.map((label) => (
                <SelectItem key={label.id} value={String(label.id)}>{label.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case 'change_status':
      return (
        <div className="space-y-2">
          <Label className="text-xs">Status</Label>
          <Select value={params.status || ''} onValueChange={(v) => onChange('status', v)}>
            <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
            <SelectContent>
              {isTicketEvent ? (
                <>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="waiting_on_customer">Waiting On Customer</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </>
              ) : (
                <>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>
      );

    case 'change_priority':
      return (
        <div className="space-y-2">
          <Label className="text-xs">Priority</Label>
          <Select value={params.priority || ''} onValueChange={(v) => onChange('priority', v)}>
            <SelectTrigger><SelectValue placeholder="Select priority" /></SelectTrigger>
            <SelectContent>
              {isTicketEvent ? (
                <>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </>
              ) : (
                <>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>
      );

    case 'send_message':
      return (
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-msg`} className="text-xs">Message</Label>
          <Textarea
            id={`${idPrefix}-msg`}
            rows={3}
            value={params.message || ''}
            onChange={(e) => onChange('message', e.target.value)}
            placeholder="Message to send…"
          />
        </div>
      );

    // 2026-09-21 (Docs/TOPIC_GUARDRAIL_PLAN.md) -- blocks the AI from
    // answering this turn at all; reply_text is sent instead. Only takes
    // effect when this is the first, single-action rule matching this
    // signal (see AutomationEngine.find_immediate_single_action_match) --
    // a rule with more than this one action never gets this treatment,
    // same constraint request_profile_field's own inline-clubbing has.
    case 'ignore_ai_message':
      return (
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-ignore-reply`} className="text-xs">Reply text</Label>
          <Textarea
            id={`${idPrefix}-ignore-reply`}
            rows={3}
            value={params.reply_text || ''}
            onChange={(e) => onChange('reply_text', e.target.value)}
            placeholder="e.g. I'm a health information assistant and can't help with that — is there something health-related I can help you with?"
          />
          <p className="text-xs text-muted-foreground">
            Sent instead of a normal AI-generated reply — the AI never answers this message at all.
          </p>
        </div>
      );

    case 'notify_agent':
    case 'notify_manager':
      return (
        <div className="space-y-3">
          <Label htmlFor={`${idPrefix}-notify-msg`} className="text-xs">Message (optional)</Label>
          <Textarea
            id={`${idPrefix}-notify-msg`}
            rows={2}
            value={params.message || ''}
            onChange={(e) => onChange('message', e.target.value)}
            placeholder="Extra context for the notification…"
          />
          {/* 2026-08-09: overrides the account-wide default (Automation ->
              Agent notifications) for just this rule — different tickets
              can warrant a different template/number, e.g. a crisis
              escalation vs a routine assignment. */}
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">Template (optional override)</Label>
              <Select
                value={params.template_id != null ? String(params.template_id) : 'default'}
                onValueChange={(v) => onChange('template_id', v === 'default' ? null : Number(v))}
              >
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Account default</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.name}{t.language ? ` · ${t.language}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Channel (optional override)</Label>
              <Select
                value={params.channel_id != null ? String(params.channel_id) : 'default'}
                onValueChange={(v) => onChange('channel_id', v === 'default' ? null : Number(v))}
              >
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Account default</SelectItem>
                  {channels.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      );

    case 'escalate_ticket':
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs">Escalate to priority</Label>
            <Select value={params.priority || 'high'} onValueChange={(v) => onChange('priority', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-escalate-reason`} className="text-xs">Reason (optional)</Label>
            <Input
              id={`${idPrefix}-escalate-reason`}
              value={params.reason || ''}
              onChange={(e) => onChange('reason', e.target.value)}
            />
          </div>
        </div>
      );

    case 'create_follow_up_ticket':
      return (
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-followup-cat`} className="text-xs">Follow-up category ID (optional)</Label>
          <Input
            id={`${idPrefix}-followup-cat`}
            value={params.category_id || ''}
            onChange={(e) => onChange('category_id', e.target.value)}
            placeholder="Same category if left blank"
          />
        </div>
      );

    case 'set_ai_enabled':
      // 2026-08-10: Docs/DIGITAL_COMPANION_AI_HANDOFF_PLAN.md §2.7 — turns AI
      // on/off for the conversation behind whichever ticket/event triggered
      // this rule. Idempotent on the backend, so re-running the rule is safe.
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs">AI</Label>
            <Select
              value={params.enabled === false ? 'off' : 'on'}
              onValueChange={(v) => onChange('enabled', v === 'on')}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Turn off (hand off to a human)</SelectItem>
                <SelectItem value="on">Turn on</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-ai-reason`} className="text-xs">Reason (optional)</Label>
            <Input
              id={`${idPrefix}-ai-reason`}
              value={params.reason || ''}
              onChange={(e) => onChange('reason', e.target.value)}
              placeholder="Shown in the conversation's escalation history"
            />
          </div>
        </div>
      );

    case 'call_agent':
      // 2026-08-31: urgently notifies the target agent (in-app + WhatsApp)
      // and surfaces the customer's phone number so they can call right
      // back — reuses TicketingService.notify_agent_assigned(urgent=True),
      // the same pipeline the live-call "talk to a human" escalation uses.
      // Leaving Agent blank falls back to the ticket's current assignee at
      // run time (see automation_actions.py's call_agent handler), so a
      // rule that already has an Assign Agent step ahead of this one
      // doesn't need the same agent picked twice.
      return (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label className="text-xs">Agent (optional)</Label>
            <Select
              value={params.agent_id != null && params.agent_id !== '' ? String(params.agent_id) : 'assigned'}
              onValueChange={(v) => onChange('agent_id', v === 'assigned' ? '' : Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="assigned">Whoever the ticket is assigned to</SelectItem>
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={String(agent.id)}>
                    {agent.first_name} {agent.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            Sends an urgent notification with the customer's number — the agent calls them back from the dashboard or WhatsApp.
          </p>
        </div>
      );

    case 'request_profile_field': {
      // 2026-09-09 (Document Vault UI redesign). isFileAnswer covers every
      // non-text answer type; "photo" is kept as a live value (not
      // remapped) so rules saved before this redesign still render/behave
      // identically -- it's just relabeled "Image" below.
      //
      // 2026-09-10 (user: "too long and too confusing... can we make it
      // look cool"): visual rework only, same params/behavior throughout --
      // card-style toggles instead of plain buttons, icon pills instead of
      // a dropdown, every "why" sentence moved into a Hint tooltip instead
      // of sitting on the page permanently.
      const isFileAnswer = ['photo', 'image', 'pdf', 'word'].includes(params.answer_type || '');
      const isVault = params.save_as === 'vault';
      const noExtractableFields = IMAGE_TYPES_WITH_NO_EXTRACTABLE_FIELDS.includes((params.document_type || '').trim());
      const answerTypes: { value: string; label: string; icon: React.ReactNode }[] = [
        { value: 'text', label: 'Text', icon: <MessageSquare className="h-3.5 w-3.5" /> },
        { value: 'photo', label: 'Image', icon: <ImageIcon className="h-3.5 w-3.5" /> },
        { value: 'pdf', label: 'PDF', icon: <FileText className="h-3.5 w-3.5" /> },
        { value: 'word', label: 'Word', icon: <FileType2 className="h-3.5 w-3.5" /> },
      ];
      const currentAnswerType = params.answer_type || 'text';

      return (
        <TooltipProvider delayDuration={200}>
          <div className="space-y-4">
            {/* What is this capturing? -- two option cards instead of
                plain buttons + a permanent paragraph; the "why" lives in
                each card's own Hint now. Defaults to "profile" (unset
                param) so every existing rule keeps today's exact
                behavior. */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">What is this capturing?</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => onChange('save_as', 'profile')}
                  className={`flex flex-col items-center gap-1 rounded-lg border px-3 py-2.5 text-xs transition-colors ${
                    !isVault ? 'border-primary bg-primary/5 text-foreground' : 'border-border text-muted-foreground hover:bg-muted/50'
                  }`}
                >
                  <User className="h-4 w-4" />
                  <span className="font-medium">Profile field</span>
                </button>
                <button
                  type="button"
                  onClick={() => onChange('save_as', 'vault')}
                  className={`flex flex-col items-center gap-1 rounded-lg border px-3 py-2.5 text-xs transition-colors ${
                    isVault ? 'border-primary bg-primary/5 text-foreground' : 'border-border text-muted-foreground hover:bg-muted/50'
                  }`}
                >
                  <Archive className="h-4 w-4" />
                  <span className="font-medium">Vault</span>
                </button>
              </div>
              <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                {isVault ? 'Kept as dated document history.' : "Merges into the customer's profile."}
                <Hint text={isVault
                  ? "Each upload is saved as a new, dated document instead of overwriting the customer's profile — repeat uploads (e.g. successive lab reports) build up as history."
                  : "Extracted or typed answers are merged straight into the customer's profile (name, age, etc.) — today's default behavior."} />
              </p>
            </div>

            {isVault && (
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1 text-xs font-medium">
                  Document type
                  <Hint text="Pick a known image type to guarantee its vision model actually picks it up, or 'Other…' for a plain vault document (id_card, prescription, ...) with no model attached. Reusing an existing type adds another entry to its history." />
                </Label>
                <DocumentTypeField
                  value={params.document_type || ''}
                  onChange={(v) => onChange('document_type', v)}
                  idPrefix={idPrefix}
                />
                <VisionRoutingHint documentType={params.document_type || ''} />
                {!isFileAnswer && (
                  <p className="flex items-center gap-1.5 text-[11px] text-amber-600">
                    <AlertTriangle className="h-3 w-3 shrink-0" />
                    Needs a file answer below (Image/PDF/Word) to actually reach the vault.
                  </p>
                )}
              </div>
            )}

            {/* Expected answer -- icon pills instead of a plain dropdown.
                2026-09-08: a text-only reply ("hi") to a photo question was
                previously accepted as the literal answer -- rejecting that
                is still the runtime behavior, just explained via Hint now. */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Expected answer</Label>
              <div className="flex flex-wrap gap-1.5">
                {answerTypes.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChange('answer_type', opt.value)}
                    className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-colors ${
                      currentAnswerType === opt.value ? 'border-primary bg-primary/5 text-foreground' : 'border-border text-muted-foreground hover:bg-muted/50'
                    }`}
                  >
                    {opt.icon}
                    {opt.label}
                  </button>
                ))}
              </div>
              {isFileAnswer && (
                <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  Text-only replies are rejected until a file arrives.
                  <Hint text={`A text-only reply won't be accepted as the answer — the customer is nudged again until they send a ${params.answer_type === 'pdf' ? 'PDF' : params.answer_type === 'word' ? 'Word document' : 'photo'} (or decline).`} />
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Message to send</Label>
              <Textarea
                placeholder="e.g. Could you share a photo of your ID card so we can personalize your support?"
                value={params.prompt_text || ''}
                onChange={(e) => onChange('prompt_text', e.target.value)}
                rows={3}
              />
            </div>

            {!isVault && (
              <div className="space-y-1.5">
                {/* 2026-09-07: datalist, not a Select -- suggests common keys
                    but still accepts anything typed. idPrefix (not useId)
                    for the list id: this switch's case blocks render inside
                    one component, so a hook call would fire conditionally on
                    only this action type, violating the Rules of Hooks.
                    2026-09-09: hidden entirely for Vault -- automation_
                    actions.py derives it from document_type instead. */}
                <Label className="text-xs font-medium">Field to capture</Label>
                <Input
                  list={`${idPrefix}-field-key-suggestions`}
                  placeholder="e.g. name, age, id_card_photo"
                  value={params.field_key || ''}
                  onChange={(e) => onChange('field_key', e.target.value)}
                />
                <datalist id={`${idPrefix}-field-key-suggestions`}>
                  {COMMON_PROFILE_FIELD_KEYS.map((key) => (
                    <option key={key} value={key} />
                  ))}
                </datalist>
              </div>
            )}

            {isFileAnswer && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">Fields to extract</Label>
                  {noExtractableFields ? (
                    <span className="text-[10px] text-muted-foreground">handled automatically</span>
                  ) : (
                    <span className="text-[10px] text-muted-foreground">optional</span>
                  )}
                </div>
                {noExtractableFields ? (
                  // 2026-09-10 (user: "for xray or skin there are no fields
                  // to be extracted, how does it supposed to reflect it
                  // there?"). The input itself is hidden -- typing field
                  // names here for a skin/X-ray photo would spend a real
                  // OCR/LLM call finding nothing; VisionRoutingHint above
                  // already covers what actually happens instead.
                  <p className="text-[11px] text-muted-foreground">
                    This document type has no text fields — see the note above.
                  </p>
                ) : (
                  <>
                    {/* 2026-09-08 (user: "is there configuration for
                        this?"). Comma-separated field_keys, free text --
                        OCR + LLM extraction is attempted for exactly these
                        fields, and only when this is set. */}
                    <Input
                      list={`${idPrefix}-extract-fields-suggestions`}
                      placeholder="e.g. name, age, gender, address, id_number"
                      value={params.extract_fields || ''}
                      onChange={(e) => onChange('extract_fields', e.target.value)}
                    />
                    <datalist id={`${idPrefix}-extract-fields-suggestions`}>
                      {COMMON_PROFILE_FIELD_KEYS.map((key) => (
                        <option key={key} value={key} />
                      ))}
                    </datalist>
                    <p className="text-[11px] text-muted-foreground">
                      Leave blank to just capture the file with no extraction.
                    </p>
                  </>
                )}
              </div>
            )}

            <Separator />

            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={!!params.skip_if_known}
                  onChange={(e) => onChange('skip_if_known', e.target.checked)}
                  disabled={isVault}
                />
                Don't ask again if already known
                {isVault && <Hint text="Not applicable to Vault — every upload is kept, nothing to skip." />}
              </label>

              {isFileAnswer && (
                // 2026-09-10 (plan 08 Phase 1e, "we won't force user, we
                // will simply prompt user that they can share"). Default
                // UNCHECKED = today's exact behavior for every rule saved
                // before this existed.
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={!params.blocking && params.blocking !== undefined}
                    onChange={(e) => onChange('blocking', !e.target.checked)}
                  />
                  Don't force an answer
                  <Hint text="The customer can keep chatting normally instead of being nudged — the file still gets captured correctly whenever it actually arrives." />
                </label>
              )}
            </div>

            <p className="text-[11px] text-muted-foreground">
              The next reply is captured automatically. "skip" / "no" / "not now" records a decline.
            </p>
          </div>
        </TooltipProvider>
      );
    }

    case 'wait': {
      // Stored as a single duration_minutes number (matches the
      // sla_response_minutes convention used elsewhere in this app) — the
      // unit selector here is just a display convenience that converts to/
      // from minutes on the way in and out.
      //
      // 2026-09-10 (user: "wait time object does not have seconds"): added
      // a Seconds option. duration_minutes can now be fractional (e.g. 0.5
      // for 30 seconds) — the schema validator already accepted a float,
      // the real bug was automation_engine.py's countdown math truncating
      // it, fixed there separately. Internally this works in whole SECONDS
      // (Math.round'd) rather than minutes, so a value entered/displayed
      // as seconds round-trips exactly instead of drifting through a
      // minutes-fraction conversion.
      const totalSeconds = Math.round((Number(params.duration_minutes) || 0) * 60);
      const unit: 'seconds' | 'minutes' | 'hours' | 'days' =
        totalSeconds > 0 && totalSeconds % 86400 === 0 ? 'days'
        : totalSeconds > 0 && totalSeconds % 3600 === 0 ? 'hours'
        : totalSeconds > 0 && totalSeconds % 60 === 0 ? 'minutes'
        : 'seconds';
      const unitSecondsDivisor = unit === 'days' ? 86400 : unit === 'hours' ? 3600 : unit === 'minutes' ? 60 : 1;
      const amount = totalSeconds / unitSecondsDivisor;
      const setFromSeconds = (seconds: number) => onChange('duration_minutes', Math.max(0, seconds) / 60);
      return (
        <div className="space-y-2">
          <Label className="text-xs">Wait for</Label>
          <div className="flex gap-2">
            <Input
              type="number"
              min={1}
              className="w-24"
              value={amount || ''}
              onChange={(e) => {
                const n = Number(e.target.value) || 0;
                setFromSeconds(n * unitSecondsDivisor);
              }}
            />
            <Select
              value={unit}
              onValueChange={(v) => {
                const divisor = v === 'days' ? 86400 : v === 'hours' ? 3600 : v === 'minutes' ? 60 : 1;
                setFromSeconds((amount || 0) * divisor);
              }}
            >
              <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="seconds">Seconds</SelectItem>
                <SelectItem value="minutes">Minutes</SelectItem>
                <SelectItem value="hours">Hours</SelectItem>
                <SelectItem value="days">Days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            Pauses here, then continues with whatever comes next in this chain.
          </p>
        </div>
      );
    }

    default:
      return null;
  }
};

export default AutomationActionParamsEditor;
